/**
 * Edit a user's role assignments.
 *
 * Wipe-and-replace semantics: whatever's checked at submit time
 * becomes the user's full role set on the server. The shared
 * RoleCheckboxList enforces the admin-wildcard rule: if Administrator
 * is selected, non-admin roles are hidden because they'd be redundant.
 */

import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { updateUserRoles, type RoleWithUsage, type UserListItem } from '../../api/admin';
import { RoleCheckboxList } from './RoleCheckboxList';

const INK = '#0f0a07';
const IVORY_LIGHT = '#fdf9f0';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

interface EditRolesDialogProps {
  open: boolean;
  user: UserListItem;
  roles: RoleWithUsage[];
  onClose: () => void;
}

export function EditRolesDialog({ open, user, roles, onClose }: EditRolesDialogProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(() => new Set(user.roles.map((r) => r.id)));

  // The parent reuses one dialog instance across rows, so we have to
  // re-seed the selection whenever a different user is opened.
  useEffect(() => {
    if (open) setSelected(new Set(user.roles.map((r) => r.id)));
  }, [open, user.id, user.roles]);

  const mutation = useMutation({
    mutationFn: () => updateUserRoles(user.id, { role_ids: Array.from(selected) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            position: 'relative',
            bgcolor: IVORY_LIGHT,
            backgroundImage: 'none',
            border: '1px solid rgba(187, 140, 63, 0.22)',
            borderRadius: 2,
            m: { xs: 2, sm: 4 },
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
              zIndex: 1,
            },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 17,
          color: INK,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          pt: 2.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'rgba(187, 140, 63, 0.12)',
            color: GOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BadgeOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box>Edit Roles</Box>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11.5,
              fontWeight: 500,
              color: 'rgba(15, 10, 7, 0.55)',
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user.first_name} {user.last_name} ·{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {user.email}
            </Box>
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ borderColor: 'rgba(187, 140, 63, 0.22)', bgcolor: IVORY_LIGHT }}
      >
        <Stack spacing={1.5}>
          {mutation.error && (
            <Alert severity="error" sx={{ fontFamily: FONT_INTER, borderRadius: 1.25 }}>
              {(mutation.error as Error).message}
            </Alert>
          )}
          <RoleCheckboxList roles={roles} selected={selected} onChange={setSelected} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button disabled={mutation.isPending} onClick={() => mutation.mutate()} sx={goldButtonSx}>
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const goldButtonSx = {
  background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
  color: INK,
  fontFamily: FONT_INTER,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  fontSize: 12,
  px: 2.5,
  py: 0.85,
  boxShadow: 'none',
  borderRadius: 1,
  '&:hover': {
    background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
    boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
  },
  '&.Mui-disabled': {
    background: 'rgba(15, 10, 7, 0.08)',
    color: 'rgba(15, 10, 7, 0.3)',
  },
} as const;

const cancelButtonSx = {
  fontFamily: FONT_INTER,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  fontSize: 12,
  px: 2,
  color: 'rgba(15, 10, 7, 0.6)',
  '&:hover': { bgcolor: 'rgba(15, 10, 7, 0.04)' },
} as const;
